/**
 * Offline Banner Component
 * Shows when the user loses internet connection
 */

import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, X } from 'lucide-react';
import { useOfflineDetector } from './offlineDetector';

export default function OfflineBanner({ theme = 'dark' }) {
  const { isOnline, wasOffline } = useOfflineDetector();
  const [showBanner, setShowBanner] = useState(false);
  const [showReconnected, setShowReconnected] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setShowBanner(true);
      setShowReconnected(false);
    } else if (wasOffline && isOnline) {
      // Just came back online
      setShowBanner(false);
      setShowReconnected(true);

      // Hide reconnected message after 3 seconds
      const timer = setTimeout(() => {
        setShowReconnected(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  const isDark = theme === 'dark';

  // Reconnected banner
  if (showReconnected) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 animate-slide-down">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-2 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm font-medium">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.75 19.5a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            You're back online!
          </div>
        </div>
      </div>
    );
  }

  // Offline banner
  if (!showBanner) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 animate-slide-down">
      <div className={`py-2.5 px-4 ${
        isDark
          ? 'bg-gradient-to-r from-amber-600 to-orange-600'
          : 'bg-gradient-to-r from-amber-500 to-orange-500'
      } text-white shadow-lg`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <WifiOff size={18} />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            </div>
            <div>
              <p className="text-sm font-semibold">You're offline</p>
              <p className="text-xs opacity-80">Some features may be limited</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-xs font-medium transition-colors"
            >
              <RefreshCw size={12} />
              Retry
            </button>
            <button
              onClick={() => setShowBanner(false)}
              className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
